import { inject, Injectable } from '@angular/core';
import { concat, merge, Observable, tap } from 'rxjs';
import { Fragment, PostFragment, PutFragment } from 'src/app/interfaces/api/fragment';
import { Link, PostLink, PutLink } from 'src/app/interfaces/api/link';
import { Metric } from 'src/app/interfaces/api/metric';
import { Planning, PostPlanning, PutPlanning } from 'src/app/interfaces/api/planning';
import { PlanningTask, PostTask, PutTask } from 'src/app/interfaces/api/task';
import { QueryParams } from 'src/app/interfaces/shared/query';
import { PlanningStore } from 'src/app/services/stores/planning.store';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../core/api.service';

@Injectable({
  providedIn: 'root'
})
export class PlanningService {

  collection = "Planning";
  collectionPath = "plannings";
  plannings: Planning[] = [];
  apiUrl = environment.API_URL;

  private readonly planningStore: PlanningStore = inject(PlanningStore)

  constructor(
    private apiService: ApiService,
  ) { }

  //---GET---//

  loadPlanning(id: string): Promise<void> {
    this.planningStore.reset();

    return new Promise((resolve, reject) => {
      concat(
        this.getPlanning(id),
        merge(
          this.getTasks({ planning: id }),
          this.getMetrics({ planning: id }),
          this.getFragments({ planning: id })
        )
      ).subscribe({
        next: () => { },
        error: (err) => reject(err),
        complete: () => {
          this.planningStore.onDataLoaded();
          resolve();
        }
      });
    });
  }

  getPlannings(query: QueryParams = {}): Observable<Planning[]> {
    return this.apiService.getCollection(this.collectionPath, "", query);
  }

  getPlanning(id: string): Observable<Planning> {
    return this.apiService.getCollectionItem(this.collectionPath, "", id)
      .pipe(tap(planning => {
        this.planningStore.updatePlanning(planning);
      })
      );
  }

  runUpdate(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'update', query);
  }

  getPlanningsCount(query: QueryParams = {}): Observable<string> {
    return this.apiService.getCollection(this.collectionPath, 'count', query);
  }

  getPlanningsLight(query: QueryParams = {}): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'light', query);
  }

  getPlanningData(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, 'data', id)
  }

  countTasks(id: string): Observable<number> {
    return this.apiService.getCollectionItem(this.collectionPath, 'tasks/count', id);
  }

  getTasks(query: QueryParams): Observable<PlanningTask[]> {
    this.planningStore.isLoading('task', true);

    return this.apiService.getCollection(this.collectionPath, 'tasks', query)
      .pipe(tap(tasks => {
        this.planningStore.updateTasks(tasks);
        this.planningStore.isLoading('task', false);
      }));
  }

  getMetrics(query: QueryParams): Observable<Metric[]> {
    this.planningStore.isLoading('metric', true);

    return this.apiService.getCollection(this.collectionPath, 'metrics', query).pipe(
      tap(metrics => {
        this.planningStore.updateMetrics(metrics);
        this.planningStore.isLoading('metric', false);
      })
    );
  }

  getFragments(query: QueryParams): Observable<Fragment[]> {
    this.planningStore.isLoading('fragment', true);

    return this.apiService.getCollection(this.collectionPath, 'fragments', query)
      .pipe(tap(fragments => {
        this.planningStore.updateFragments(fragments);
        this.planningStore.isLoading('fragment', false);
      }));
  }

  getFragmentsByTask(query: QueryParams): Observable<Fragment[]> {
    return this.apiService.getCollection(this.collectionPath, 'fragments/byTask', query);
  }

  getFragment(id: string): Observable<Fragment> {
    return this.apiService.getCollectionItem(this.collectionPath, 'fragments', id);
  }

  getFragmentPicture(query: QueryParams): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'fragments/picture', query);
  }

  resetFragments(planning: string, task: string = ""): Observable<any> {
    return this.apiService.getCollection(this.collectionPath, 'fragments/reset', { planning, task });
  }

  getPlanningExport(id: string): Observable<any> {
    return this.apiService.getCollectionItem(this.collectionPath, 'export', id);
  }

  //---POST---//

  addPlanning(planning: PostPlanning): Observable<Planning> {
    return this.apiService.postCollectionData(this.collectionPath, '', planning);
  }

  addTask(task: PostTask): Observable<PlanningTask> {
    return this.apiService.postCollectionData(this.collectionPath, 'task', task);
  }

  addTaskV2(task: PostTask): Observable<PlanningTask> {
    return this.apiService.postCollectionData(this.collectionPath, 'taskV2', task);
  }

  addTasks(tasks: PostTask[]): Observable<PlanningTask[]> {
    return this.apiService.postCollectionData(this.collectionPath, 'tasks', tasks);
  }

  addLink(link: PostLink): Observable<Link> {
    return this.apiService.postCollectionData(this.collectionPath, 'link', link);
  }

  duplicatePlanning(planning: PostPlanning): Observable<Planning> {
    return this.apiService.postCollectionData(this.collectionPath, 'duplicate', planning);
  }

  addPlanningImport(uFile: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'import', { file: uFile['file'], planning: uFile['planningId'] });
  }

  addFragment(fragment: PostFragment): Observable<Fragment> {
    return this.apiService.postCollectionData(this.collectionPath, 'fragments', fragment);
  }

  addFragments(fragments: PostFragment[]): Observable<Fragment[]> {
    return this.apiService.postCollectionData(this.collectionPath, 'fragments', fragments);
  }

  rebuildTaskFragments(infos: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'task/rebuild', infos);
  }

  autoLinkTaskFragments(infos: QueryParams): Observable<any> {
    return this.apiService.postCollectionData(this.collectionPath, 'task/autolink', infos);
  }

  //---PUT---//

  setPlanning(planning: PutPlanning): Observable<any> {
    return this.apiService.updateCollectionItem(this.collectionPath, planning);
  }

  updatePlanning(planning: PutPlanning): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, "", planning);
  }

  updateFragment(fragment: PutFragment): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'fragments' + fragment.id, fragment);
  }

  updateFragments(fragments: PutFragment[]): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'fragments', fragments);
  }

  updateTask(task: PutTask): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'task/' + task['id'], task);
  }

  updateTaskV2(task: PutTask): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'taskV2/' + task['id'], task);
  }

  updateLink(link: PutLink): Observable<any> {
    return this.apiService.updateCollection(this.collectionPath, 'link/' + link['id'], link);
  }

  //---DELETE---//

  removePlanning(planningId: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, planningId);
  }

  removeTask(taskId: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, 'task/' + taskId);
  }

  removeLink(linkId: string): Observable<any> {
    return this.apiService.deleteCollectionItem(this.collectionPath, 'link/' + linkId);
  }
}
